import {
  MRT_ColumnDef,
  MRT_PaginationState,
  MantineReactTable,
  useMantineReactTable,
  MRT_ColumnFiltersState,
  MRT_SortingState,
} from "mantine-react-table";
import { FC, useState, useMemo, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";

import { FileType } from "@funded-here-interface/common/src/constant/enum";
import { downloadFile } from "@funded-here-interface/common/src/Utils/file";
import { Box, Button } from "@mantine/core";
import { IconDownload } from "@tabler/icons-react";

import { useStyle } from "./MerchantConfiguration.styles";
import useDownloadExternalAccountConfig from "../../hooks/useDownloadExternalAccountConfig";
import useGetExternalAccountConfig from "../../hooks/useGetExternalAccountConfig";
import { ExternalAccountConfigData } from "../../services/getExternalAccountConfig";
import { RootState } from "../../store";

const FundPullAutoRefundTable: FC = () => {
  const token = useSelector((state: RootState) => state.auth.token);

  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });

  const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>(
    []
  );
  const [sorting, setSorting] = useState<MRT_SortingState>([
    {
      id: "company",
      desc: false,
    },
  ]);
  const [globalFilter, setGlobalFilter] = useState("");

  const useDownloadExternalAccountConfigMutation =
    useDownloadExternalAccountConfig();

  const apiColumnFilters: string = JSON.stringify(
    columnFilters
      .filter((filter) => {
        if (Array.isArray(filter.value)) {
          return filter.value.every(
            (v) => v !== null && v !== undefined && v !== ""
          );
        }
        return (
          filter.value !== null &&
          filter.value !== undefined &&
          filter.value !== ""
        );
      })
      .map((filter) => ({
        [filter.id]: filter.value,
      }))
  );

  const apiSorting: string = JSON.stringify(
    sorting.reduce((acc, sort) => {
      if (sort.id && sort.desc !== undefined) {
        acc[sort.id] = sort.desc;
      }
      return acc;
    }, {} as Record<string, boolean>)
  );

  const { data, error, isLoading } = useGetExternalAccountConfig(
    token,
    pagination,
    apiColumnFilters,
    apiSorting,
    globalFilter
  );

  useEffect(() => {
    if (error) {
      toast.error((error as Error).message);
    }
  }, [error]);

  const totalRowCount = data?.totalRowCount ?? 0;

  const handleDownloadExternalAccountConfig = useCallback(() => {
    useDownloadExternalAccountConfigMutation.mutate(
      {
        token,
        columnFilters: apiColumnFilters,
        sorting: apiSorting,
        globalFilter,
      },
      {
        onSuccess: (data: Blob) => {
          downloadFile(data, "ExternalAccountConfig", FileType.CSV);
        },
        onError: (e) => {
          toast.error((e as Error).message);
        },
      }
    );
  }, [
    apiColumnFilters,
    apiSorting,
    globalFilter,
    token,
    useDownloadExternalAccountConfigMutation,
  ]);

  const columns = useMemo<MRT_ColumnDef<ExternalAccountConfigData>[]>(
    () => [
      {
        accessorKey: "company",
        header: "Merchant",
        Cell: ({ renderedCellValue }) => renderedCellValue ?? "—",
      },
      {
        accessorKey: "email",
        header: "Merchant Email",
        Cell: ({ renderedCellValue }) => renderedCellValue ?? "—",
      },
      {
        accessorKey: "isFundPullEnabled",
        header: "Fund Pull",
        filterFn: "equals",
        mantineFilterSelectProps: {
          data: [
            { value: "true", label: "Enabled" },
            { value: "false", label: "Disabled" },
          ],
        },
        filterVariant: "select",
        Cell: ({ cell }) => (cell.getValue<boolean>() ? "Enabled" : "Disabled"),
      },
      {
        accessorKey: "isAutoRefundEnabled",
        header: "Auto-Refund",
        filterFn: "equals",
        mantineFilterSelectProps: {
          data: [
            { value: "true", label: "Enabled" },
            { value: "false", label: "Disabled" },
          ],
        },
        filterVariant: "select",
        Cell: ({ cell }) => (cell.getValue<boolean>() ? "Enabled" : "Disabled"),
      },
    ],
    []
  );

  const table = useMantineReactTable({
    enableColumnActions: true,
    enableColumnFilters: true,
    enableDensityToggle: false,
    enableMultiSort: true,
    paginationDisplayMode: "pages",
    state: {
      pagination,
      isLoading,
      sorting,
      globalFilter,
      columnFilters,
    },
    columns,
    data: data?.data ?? [],
    manualPagination: true,
    manualSorting: true,
    manualFiltering: true,
    onPaginationChange: setPagination,
    onColumnFiltersChange: setColumnFilters,
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    rowCount: totalRowCount,
    initialState: { density: "xs" },
    mantineTableProps: {
      withColumnBorders: true,
      withBorder: true,
    },
    mantineTableHeadCellProps: {
      style: {
        padding: "0.5rem 1.0rem",
        color: "#23262a",
      },
    },
    mantineTableBodyCellProps: {
      style: {
        padding: "0.5rem",
      },
    },
    renderTopToolbarCustomActions: ({ table }) => (
      <Box
        sx={{
          display: "flex",
          gap: "16px",
          padding: "8px",
          flexWrap: "wrap",
        }}
      >
        <Button
          color="lightblue"
          onClick={handleDownloadExternalAccountConfig}
          leftIcon={<IconDownload />}
          variant="filled"
        >
          Export CSV
        </Button>
      </Box>
    ),
  });

  return <MantineReactTable table={table} />;
};

const FundPullAutoRefund = () => {
  const { classes } = useStyle();
  return (
    <div>
      <h1 className={classes.headerText}>Fund Pull / Auto Refund</h1>

      <FundPullAutoRefundTable />
    </div>
  );
};

export default FundPullAutoRefund;
